import { useEffect, useRef, useState } from 'react'
import type { LayoutChangeEvent } from 'react-native'
import { Easing, useAnimatedStyle, useSharedValue, withTiming, type WithTimingConfig } from 'react-native-reanimated'

import { useIsKeyboardVisible } from '@/src/general/hooks/useIsKeyboardVisible'
import { useTimeout } from '@/src/general/hooks/useTimeout'
import { useTrackDisplayed } from '@/src/general/hooks/useTrackOnce'
import { assignAnimatedValue } from '@/src/general/utils/assignAnimatedValue'
import { EVENTS } from '@/src/general/utils/gtm/constants'

import type { TGetAlertBannerData } from '../../../api/types/alert'

const SHOW_ALERT_START_UP_DELAY = 4000

const timingConfiguration: WithTimingConfig = { duration: 700, easing: Easing.bezier(0.5, 0.01, 0, 1) }

export const useBannerAnimation = (data: TGetAlertBannerData | undefined, cacheData: Record<number, boolean>) => {
  const translateY = useSharedValue(-220)
  const heightY = useSharedValue(0)
  const layoutRef = useRef({ messageHeight: 220 })
  const [isReadyToShow, setIsReadyToShow] = useState(false)
  const isKeyboardVisible = useIsKeyboardVisible()

  const animatedHeightY = useAnimatedStyle(() => ({
    height: heightY.value,
  }))

  const animatedY = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }))

  useTimeout(() => setIsReadyToShow(true), SHOW_ALERT_START_UP_DELAY)

  const showAlert = data && !cacheData[data.id] && isReadyToShow && !isKeyboardVisible

  useTrackDisplayed(EVENTS.ALERT_BANNER_DISPLAYED, showAlert ? data?.id : undefined, { bannerName: data?.title })

  useEffect(() => {
    if (showAlert) {
      assignAnimatedValue(heightY, withTiming(layoutRef.current.messageHeight, timingConfiguration))
      assignAnimatedValue(translateY, withTiming(0, timingConfiguration))
    } else {
      assignAnimatedValue(heightY, withTiming(0, timingConfiguration))
      assignAnimatedValue(
        translateY,
        withTiming(translateY.value - layoutRef.current.messageHeight - 100, timingConfiguration),
      )
    }
  }, [showAlert, translateY, heightY, layoutRef])

  const onLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    // Update alert position when window size is updated
    if (!layoutRef.current.messageHeight && layout.height > 0) {
      layoutRef.current.messageHeight = layout.height
      assignAnimatedValue(heightY, withTiming(-100 - layoutRef.current.messageHeight))
    }
    if (layoutRef.current.messageHeight !== layout.height && layout.height > 0) {
      layoutRef.current.messageHeight = layout.height
      if (showAlert) {
        assignAnimatedValue(heightY, withTiming(layoutRef.current.messageHeight))
      } else {
        assignAnimatedValue(heightY, withTiming(0, timingConfiguration))
        assignAnimatedValue(translateY, withTiming(-100 - layoutRef.current.messageHeight, timingConfiguration))
      }
    }
  }
  return { animatedY, translateY, animatedHeightY, layoutRef, onLayout }
}
